import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Typography, 
    RadioGroup, 
    FormControlLabel, 
    Radio,
    Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChooseQuestionDialog from './ChooseQuestionDialog';
import InPersonSignatureDialog from './InPersonSignatureDialog';
import * as Actions from '../../redux/actions';

const useStyles = makeStyles(theme => ({
    title: {
        fontFamily: 'Assistant',        
        color: 'white',
        backgroundColor: theme.palette.primary[700],
        height: '24px',
        '& > .MuiTypography-root': {
            fontSize: '12px',
            fontWeight: 500,
        }
    },
    signatureTypeSection: {
        marginBottom: theme.spacing(1)
    },
    typeDescription: {
        fontFamily: 'Assistant',
        fontSize: '10px',
        color: '#666',
        marginTop: theme.spacing(0.5),
        marginLeft: theme.spacing(3)
    }
}));

export default function SignatureSetup(props) {
    const classes = useStyles();
    const containerId = useSelector(state => state.document.setupSignatureContainerId);
    const document = useSelector(state => state.document);
    const [showRemoteDialog, setShowRemoteDialog] = useState(false);
    const [showInPersonDialog, setShowInPersonDialog] = useState(false);
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    
    const dispatch = useDispatch();

    useEffect(() => {
        if (containerId && !isInitialized) {
            const docSignatureType = document.signature_type || 'docusign';
            console.log('SignatureSetup: containerId=', containerId, 'signature_type=', docSignatureType, 'document=', document);
            
            if (docSignatureType === 'in_person') {
                console.log('Showing in-person dialog');
                setShowInPersonDialog(true);
                setShowRemoteDialog(false);
                setShowTypeSelector(false);
            } else if (docSignatureType === 'draw' || docSignatureType === 'docusign') {
                console.log('Showing remote dialog');
                setShowRemoteDialog(true);
                setShowInPersonDialog(false);
                setShowTypeSelector(false);
            } else {
                console.log('Showing type selector as fallback');
                setShowTypeSelector(true);
                setShowRemoteDialog(false);
                setShowInPersonDialog(false);
            }
            setIsInitialized(true);
        }
    }, [containerId, document.signature_type, isInitialized]);

    useEffect(() => {
        if (containerId && isInitialized) {
            const timer = setTimeout(() => {
                if (!showRemoteDialog && !showInPersonDialog && !showTypeSelector) {
                    console.log('Fallback: showing remote dialog');
                    setShowRemoteDialog(true);
                }
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [containerId, isInitialized, showRemoteDialog, showInPersonDialog, showTypeSelector]);

    const handleTypeSelection = (type) => {
        if (type === 'remote') {
            setShowTypeSelector(false);
            setShowRemoteDialog(true);
        } else {
            setShowTypeSelector(false);
            setShowInPersonDialog(true);
        }
    };

    const onRemoteQuestionSelected = (selectedQuestion, parentQuestion) => {
        dispatch(Actions.saveSignature(containerId, selectedQuestion, parentQuestion));
        onClose();
    };

    const onInPersonSave = (signatureData) => {
        dispatch(Actions.saveInPersonSignature(containerId, signatureData));
        onClose();
    };

    const onClose = () => {
        setIsInitialized(false);
        setShowRemoteDialog(false);
        setShowInPersonDialog(false);
        setShowTypeSelector(false);
        dispatch(Actions.setupSignature(null));
    };

    const handleRemoteDialogClose = () => {
        onClose();
    };

    const handleInPersonDialogClose = () => {
        onClose();
    };

    const handleTypeSelectorClose = () => {
        onClose();
    };

    if (!containerId) {
        return null;
    }

    return (
        <>
            {showTypeSelector && (
                <Dialog open={true} onClose={handleTypeSelectorClose} maxWidth="xs" fullWidth>
                    <DialogTitle className={classes.title}>
                        Signature Setup
                    </DialogTitle>
                    <DialogContent>
                        <Box className={classes.signatureTypeSection}>
                            <Typography variant="subtitle2" style={{fontSize: '12px'}} gutterBottom>
                                Choose Signature Type
                            </Typography>
                            <RadioGroup 
                                defaultValue="remote"
                                onChange={(e) => handleTypeSelection(e.target.value)}
                            >
                                <FormControlLabel 
                                    value="remote" 
                                    control={<Radio />} 
                                    label="Remote Signature" 
                                />
                                <Typography className={classes.typeDescription}>
                                    Signer receives email and signs document electronically from their location
                                </Typography>
                                
                                <FormControlLabel 
                                    value="in_person" 
                                    control={<Radio />} 
                                    label="In-Person Signature" 
                                />
                                <Typography className={classes.typeDescription}>
                                    Host facilitates signing session with signer physically present
                                </Typography>
                            </RadioGroup>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleTypeSelectorClose} size="small">
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <ChooseQuestionDialog 
                open={showRemoteDialog}
                onClose={handleRemoteDialogClose}
                onQuestionSelected={onRemoteQuestionSelected}
                filter={q => q.type === 'email'}
            />

            <InPersonSignatureDialog
                open={showInPersonDialog}
                onClose={handleInPersonDialogClose}
                onSave={onInPersonSave}
            />
        </>
    );
}
